import { useSelector } from 'react-redux';
import { Fragment, useEffect, useState } from 'react';

import Box from '@mui/material/Box';

import SeatGeekModal from './SeatGeekModal';
import CredentailsCard from './CredentialsCard';
import VividSeatsModal from './VividSeatsModal';
import { snackAlertData } from 'utils/constants';
import AutomatiqB2BModal from './AutomatiqB2BModal';
import { CircleLoading, SnackBarAlert } from 'shared';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { integrationsData } from '../../utils/integrationsData';
import { useCompanyService } from 'store/services/company.service';
import { credentailStyles } from 'components/Invoice/styles/styles';
import { useSeatGeekService } from 'store/services/seatGeek.service';
import { useVividSeatsService } from 'store/services/vividSeats.service';
import { useAutomatiqB2bService } from 'store/services/automaitqB2b.service';

const Credentials = () => {
    const classes = credentailStyles();
    const { getSeatGeekPayments } = useSeatGeekService();
    const { getCompanyIntegrations } = useCompanyService();
    const { getVividSeatsPayments } = useVividSeatsService();
    const { getAutomatiqB2bPayments } = useAutomatiqB2bService();
    const companyId = useSelector((state) => state?.company?.data?.id);

    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [companyIntegrations, setCompanyIntegrations] = useState({});

    const handleGetCompanyIntegrations = async () => {
        try {
            setLoading(true);
            const response = await getCompanyIntegrations(companyId);
            setCompanyIntegrations(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const handleConnectClick = (option, event) => {
        event.stopPropagation();
        setSelectedOption(option);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedOption(null);
    };

    const handleIntegrationSuccess = (message) => {
        if (selectedOption && selectedOption.name === 'Seat Geek') {
            getSeatGeekPayments(companyId);
        }

        if (selectedOption && selectedOption.name === 'Automatiq B2B') {
            getAutomatiqB2bPayments(companyId);
        }

        if (selectedOption && selectedOption.name === 'Vivid Seats') {
            getVividSeatsPayments(companyId);
        }

        handleSuccessAlert(setAlertData, message);
        handleCloseModal();
        handleGetCompanyIntegrations();
    };

    useEffect(() => {
        if (companyId) {
            handleGetCompanyIntegrations();
        }
    }, [companyId]);

    return (
        <Fragment>
            {loading ? (
                <CircleLoading />
            ) : (
                <Box sx={classes.root}>
                    {integrationsData(companyIntegrations).map((integration) => (
                        <Box width="100%" key={integration.name}>
                            <CredentailsCard
                                name={integration.name}
                                logo={integration.logo}
                                description={integration.portal}
                                lastSync={integration.lastSync}
                                isConnected={integration.isConnected}
                                onConnectClick={handleConnectClick}
                            />
                        </Box>
                    ))}
                    {modalOpen && selectedOption && selectedOption.name === 'Vivid Seats' && (
                        <VividSeatsModal
                            open={modalOpen}
                            onClose={handleCloseModal}
                            onSuccess={handleIntegrationSuccess}
                            isConnected={
                                companyIntegrations.integrations_data.vivid_seats_connected
                            }
                        />
                    )}
                    {modalOpen && selectedOption && selectedOption.name === 'Automatiq B2B' && (
                        <AutomatiqB2BModal
                            open={modalOpen}
                            onClose={handleCloseModal}
                            onSuccess={handleIntegrationSuccess}
                            isConnected={
                                companyIntegrations.integrations_data.automatiq_b2b_connected
                            }
                        />
                    )}
                    {modalOpen && selectedOption && selectedOption.name === 'Seat Geek' && (
                        <SeatGeekModal
                            open={modalOpen}
                            onClose={handleCloseModal}
                            onSuccess={handleIntegrationSuccess}
                            isConnected={companyIntegrations.integrations_data.seat_geek_connected}
                        />
                    )}
                </Box>
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default Credentials;
